import axios from 'axios';
// 修改为新的API导入路径
import { getCurrentUser } from '../api/utils';
import { getToken } from './authService';
import Logger from '../utils/logger';

// 创建axios实例
const axiosInstance = axios.create({
  timeout: 60000, // 60秒超时（与api.js中的fetch超时保持一致）
  headers: {
    'Content-Type': 'application/json',
  }
});

// 请求拦截器
axiosInstance.interceptors.request.use(
  (config) => {
    // 修复：确保从正确的位置获取token
    const currentUser = getCurrentUser();
    const token = currentUser?.token;

    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    
    // 为特定API添加用户ID到请求体
    // 后端已优化为"API调用成功后再扣积分"，现在可以安全传递userId
    if (currentUser?.id && config.data && 
        (
          config.url.includes('/proxy/model') ||
          config.url.includes('/user/') ||
          config.url.includes('/showcase/create') ||
          config.url.includes('/showcase/save-generated')
        )) {
      config.data.userId = currentUser.id;
    }
    
    Logger.debug('请求拦截器', { url: config.url, method: config.method });
    return config;
  },
  (error) => {
    Logger.error('请求拦截器错误', error);
    return Promise.reject(error);
  }
);

// 响应拦截器
axiosInstance.interceptors.response.use(
  (response) => {
    // 处理统一响应格式
    const responseData = response.data;
    
    // 检查响应数据是否存在且是对象
    if (!responseData || typeof responseData !== 'object') {
      Logger.warn('API响应数据为空或格式异常', { url: response.config.url, data: responseData });
      return {
        success: false,
        message: 'API响应数据异常',
        data: null
      };
    }
    
    // 检查响应数据结构是否符合新的格式
    if ('success' in responseData && 'message' in responseData) {
      // 已经是统一格式，直接返回
      return responseData;
    } else {
      // 旧格式或其他格式，包装成统一格式
      Logger.warn('API响应不符合标准格式，进行适配转换', { url: response.config.url });
      return {
        success: true, // 假设响应成功
        message: '操作成功',
        data: responseData // 将整个响应作为data返回
      };
    }
  },
  async (error) => {
    // 处理常见错误情况
    if (error.response) {
      // 服务器返回了错误状态码
      const errorMessage = error.response.data?.message || error.response.statusText || '请求失败';
      
      if (error.response.status === 401) {
        Logger.error('API密钥无效或已过期，请检查配置');
      } else if (error.response.status === 403) {
        Logger.error('API访问被拒绝，请检查权限设置');
      } else if (error.response.status === 400) {
        Logger.error('请求参数错误，请检查输入内容');
      } else if (error.response.status === 429) {
        Logger.warn('API请求被限流，请稍后再试');
      } else {
        Logger.error(`请求失败 (${error.response.status})`, error.response.data);
      }
      
      // 将错误响应也转换为统一格式
      return Promise.reject({
        success: false,
        message: errorMessage,
        data: error.response.data?.data || null
      });
    } else if (error.request) {
      // 请求已发送但未收到响应
      Logger.error('未收到服务器响应，请检查网络连接');
      return Promise.reject({
        success: false,
        message: '未收到服务器响应，请检查网络连接',
        data: null
      });
    } else {
      // 请求配置出错
      Logger.error('请求配置错误', error.message);
      return Promise.reject({
        success: false,
        message: `请求配置错误: ${error.message}`,
        data: null
      });
    }
  }
);

// 创建一个用于流式响应的特殊axios实例
export const axiosStreamInstance = axios.create({
  timeout: 1800000, // 流式响应可能经历多渠道重试
  headers: {
    'Content-Type': 'application/json',
  },
  // 注意：在浏览器中，对于SSE(Server-Sent Events)不能使用responseType: 'stream'
  // 而是需要使用responseType: 'text'，然后手动处理流式数据
  responseType: 'text'
});

// 为流式实例添加相同的请求拦截器
axiosStreamInstance.interceptors.request.use(
  axiosInstance.interceptors.request.handlers[0].fulfilled,
  axiosInstance.interceptors.request.handlers[0].rejected
);

// 添加流式响应的特殊处理拦截器
axiosStreamInstance.interceptors.response.use(
  (response) => {
    // 检测是否为 SSE 流式响应
    if (response.headers['content-type']?.includes('text/event-stream')) {
      // 此时 response.data 是一个文本字符串，而不是流
      return {
        ...response,
        data: response.data // 保持为原始文本，由调用函数处理
      };
    }
    return response;
  },
  async (error) => {
    // 特殊处理网络错误，尝试从响应中提取数据
    if (error.response && error.response.data) {
      console.warn('流式请求出错，但有部分响应数据', error.response.data);
      return {
        data: error.response.data,
        status: error.response.status,
        headers: error.response.headers,
        partialResponse: true
      };
    }
    
    return Promise.reject({
      success: false,
      message: `流式请求错误: ${error.message || '未知错误'}`,
      data: null
    });
  }
);

export default axiosInstance; 